import React from "react";
import { Layout } from "@/components/Layout";
import { PageHero, SectionHeading, RequestInfoButton } from "@/components/common";
import { OrgCard } from "@/components/OrgCard";
import { useSEO } from "@/lib/useSEO";
import { ORGANIZATIONS, LEADERSHIP } from "@/config/site";
import { Target, Compass, Lightbulb, BookOpen, ShieldCheck, Handshake, Sparkles, Heart, User } from "lucide-react";

const values = [
  { icon: Lightbulb, title: "Innovation" },
  { icon: BookOpen, title: "Learning" },
  { icon: ShieldCheck, title: "Integrity" },
  { icon: Sparkles, title: "Opportunity" },
  { icon: Handshake, title: "Practical Impact" },
  { icon: Heart, title: "Social Responsibility" },
];

export default function About() {
  useSEO({
    title: "About Us | Atmasamman Group",
    description: "Atmasamman Group is an ecosystem of organizations working across technology services, artificial intelligence and emerging-skills education, management and technology education, and social development.",
    path: "/about",
  });

  return (
    <Layout>
      <PageHero eyebrow="About Us" title="About Atmasamman Group"
        subtitle="Atmasamman Group is an ecosystem of organizations working across technology services, artificial intelligence and emerging-skills education, management and technology education, and social development." />

      {/* Our Story */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Our Story" title="One shared vision across technology and education" />
          <p className="mt-6 text-base leading-relaxed text-slate-600 sm:text-lg">
            Atmasamman Group brings together initiatives spanning technology services, artificial intelligence and future-skills education, higher education and social impact. Each organization maintains its own identity while contributing to a common purpose — creating opportunities through technology, education and empowerment.
          </p>
          <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">
            Together, these organizations form an ecosystem designed to serve individuals, professionals, businesses and communities alike.
          </p>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="bg-[#F8FAFC] py-16 md:py-24">
        <div className="mx-auto grid max-w-7xl gap-6 px-4 sm:px-6 md:grid-cols-2 md:gap-8 lg:px-8">
          <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-testid="vision-card">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-orange-500/10 text-orange-500"><Compass className="h-6 w-6" /></div>
            <h3 className="mt-5 font-display text-2xl font-bold text-slate-900">Our Vision</h3>
            <p className="mt-3 text-base leading-relaxed text-slate-600">To create opportunities through technology, education and empowerment.</p>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-testid="mission-card">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-orange-500/10 text-orange-500"><Target className="h-6 w-6" /></div>
            <h3 className="mt-5 font-display text-2xl font-bold text-slate-900">Our Mission</h3>
            <p className="mt-3 text-base leading-relaxed text-slate-600">To build an ecosystem where individuals, professionals, businesses and communities can benefit from practical skills, technology and meaningful learning.</p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading center eyebrow="Our Values" title="What guides us" />
          <div className="mt-12 grid grid-cols-2 gap-4 sm:gap-6 md:grid-cols-3 lg:grid-cols-6">
            {values.map(({ icon: Icon, title }) => (
              <div key={title} className="rounded-2xl border border-slate-200 bg-white p-5 text-center shadow-sm transition-all hover:-translate-y-1 hover:border-orange-500/40 hover:shadow-lg">
                <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-xl bg-[#0B132B] text-orange-400"><Icon className="h-5 w-5" /></div>
                <p className="mt-3 font-display text-sm font-semibold text-slate-900">{title}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ecosystem */}
      <section className="bg-[#F8FAFC] py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading center eyebrow="Our Ecosystem" title="The organizations behind the Group" />
          <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8">
            {ORGANIZATIONS.map((org) => <OrgCard key={org.code} org={org} />)}
          </div>
        </div>
      </section>

      {/* Leadership */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading center eyebrow="Leadership" title="Guiding the vision" />
          <div className="mx-auto mt-12 grid max-w-4xl grid-cols-1 gap-6">
            {LEADERSHIP.map((l) => (
              <div key={l.name} className="flex flex-col items-start gap-5 rounded-2xl border border-slate-200 bg-white p-8 shadow-sm sm:flex-row sm:items-center" data-testid="leadership-card">
                <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-[#0B132B] text-orange-400"><User className="h-8 w-8" /></div>
                <div>
                  <h3 className="font-display text-xl font-bold text-slate-900">{l.name}</h3>
                  <p className="text-sm font-semibold text-orange-600">{l.role}</p>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{l.bio}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-12 text-center">
            <RequestInfoButton interest="Atmasamman Group" label="Request Information" testId="about-request-info" />
          </div>
        </div>
      </section>
    </Layout>
  );
}
